import React, { useState } from 'react';
import FormPage from '../shared/FormPage';
import useCreateCompanyStore from '../../stores/createCompanyStore';
import { BUSINESS_TYPES } from './CompanyNamePage';
import { PRIMARY_GOAL_LABELS } from './StartingModulesPage';

// Every business type the wizard offers, so the review never reads
// "Not provided" for a choice the user actually made.
export const BUSINESS_TYPE_LABELS = Object.fromEntries(
  BUSINESS_TYPES.map((option) => [option.value, option.title])
);

const ConfirmationPage = () => {
  const { formData, previousStep, nextStep, createCompany } = useCreateCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCreateCompany = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await createCompany();

      if (result.success) {
        // Navigate to success page
        nextStep();
      } else {
        setError(result.error || 'An unexpected error occurred. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <FormPage
      title="Almost done! ✨"
      subtitle="Please review your information before creating your company"
      inputType="none"
      showNext={false}
      showPrevious={false}
    >
      <div className="space-y-5">
        {/* Company Details Card */}
        <div className="bg-gray-50/80 rounded-2xl p-4 border border-gray-100">
          <h3 className="text-base font-semibold text-gray-900 mb-3">
            Company & User Details
          </h3>

          <div className="space-y-2">
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600 font-medium text-sm">Company Name:</span>
              <span className="text-gray-900 font-semibold text-sm">
                {formData.companyName || 'Not provided'}
              </span>
            </div>

            <div className="h-px bg-gray-200"></div>

            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600 font-medium text-sm">Business Type:</span>
              <span className="text-gray-900 font-semibold text-sm">
                {BUSINESS_TYPE_LABELS[formData.businessType] || 'Not provided'}
              </span>
            </div>

            <div className="h-px bg-gray-200"></div>

            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600 font-medium text-sm">Starting with:</span>
              <span className="text-gray-900 font-semibold text-sm">
                {PRIMARY_GOAL_LABELS[formData.primaryGoal] || 'Not provided'}
              </span>
            </div>

            <div className="h-px bg-gray-200"></div>

            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600 font-medium text-sm">First Name:</span>
              <span className="text-gray-900 font-semibold text-sm">
                {formData.firstName || 'Not provided'}
              </span>
            </div>

            <div className="h-px bg-gray-200"></div>

            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600 font-medium text-sm">Last Name:</span>
              <span className="text-gray-900 font-semibold text-sm">
                {formData.lastName || 'Not provided'}
              </span>
            </div>

            <div className="h-px bg-gray-200"></div>

            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600 font-medium text-sm">Email:</span>
              <span className="text-gray-900 font-semibold text-sm">
                {formData.email || 'Not provided'}
              </span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-red-800 font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* What's Next */}
        <div className="bg-violet-50 rounded-2xl p-4 border border-violet-100">
          <h3 className="text-base font-semibold text-violet-900 mb-3 flex items-center">
            <svg className="w-4 h-4 mr-2 text-violet-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            What's Next?
          </h3>
          <p className="text-violet-800 mb-3 leading-relaxed text-sm">
            We'll create your company and admin account, then email you a secure link to create your password. That link signs you in automatically so you can continue setup in Rightwork.
          </p>
          <ul className="space-y-2 text-xs text-violet-700">
            <li className="flex items-center">
              <svg className="w-3 h-3 mr-2 text-violet-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              A default department will be created for your business type
            </li>
            <li className="flex items-center">
              <svg className="w-3 h-3 mr-2 text-violet-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Onboarding center will guide the next setup steps
            </li>
            <li className="flex items-center">
              <svg className="w-3 h-3 mr-2 text-violet-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Sample data can still be loaded later from inside the app
            </li>
          </ul>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between items-center pt-2">
          <button
            onClick={previousStep}
            className="px-6 py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors duration-200 flex items-center group rounded-xl hover:bg-gray-50"
          >
            <svg
              className="w-4 h-4 mr-2 transform group-hover:-translate-x-0.5 transition-transform duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <button
            onClick={isLoading ? undefined : handleCreateCompany}
            disabled={isLoading}
            className={`px-8 py-4 rounded-2xl font-semibold text-white transition-all duration-200 flex items-center group ${
              isLoading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-violet-700 hover:bg-violet-800 shadow-sm hover:shadow-md'
            }`}
          >
            {isLoading ? (
              <>
                <svg className="w-5 h-5 mr-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Company...
              </>
            ) : (
              <>
                Create Company
                <svg
                  className="w-4 h-4 ml-2 transform group-hover:translate-x-0.5 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </FormPage>
  );
};

export default ConfirmationPage;
